"use client";

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { EmployeeListPanel } from "../components/EmployeeListPanel";
import { PermissionListPanel } from "../components/PermissionListPanel";
import { permissionEmployeeService } from "../services/permission-employee-service";
import { Employee, Permission } from "../types";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import { useDebounce } from "../../../hooks/use-debounce";

/**
 * Component Cha: Trang Phân Quyền Nhân Viên
 * Sử dụng server-side pagination: page, limit, key_search gửi lên API
 */
const PermissionEmployeeIndexPage: React.FC = () => {
  // === STATE ===
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [originalPermissions, setOriginalPermissions] = useState<Set<number>>(new Set());
  const [stagedPermissions, setStagedPermissions] = useState<Set<number>>(new Set());
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingAllPermissions, setIsLoadingAllPermissions] = useState(false);
  const [isLoadingEmployeePermissions, setIsLoadingEmployeePermissions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [permissionSearch, setPermissionSearch] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);

  // === Danh sách bộ phận (load 1 lần, không phân trang) ===
  const [allDepartments, setAllDepartments] = useState<{ value: string; label: string }[]>([]);

  // === DEBOUNCE SEARCH ===
  const debouncedSearch = useDebounce(employeeSearch, 500);

  // === STATE PHÂN TRANG SERVER-SIDE ===
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { fetch: fetchList } = permissionEmployeeService.list();
  const { fetch: fetchListPermission } = permissionEmployeeService.listPermission();
  const { fetch: fetchListPermissionsForEmployee } = permissionEmployeeService.getPermissionsForEmployee();
  const { fetch: updatePermissionsForEmployee } = permissionEmployeeService.updatePermissionsForEmployee();

  const listRef = useRef(fetchList);
  useEffect(() => { listRef.current = fetchList; }, [fetchList]);
  const listPermissionRef = useRef(fetchListPermission);
  useEffect(() => { listPermissionRef.current = fetchListPermission; }, [fetchListPermission]);

  // === LOAD DANH SÁCH BỘ PHẬN (1 lần) ===
  useEffect(() => {
    const loadDepartments = async () => {
      const res = await listRef.current({ status: 1, limit: 500, page: 1 });
      if (res && isSuccess(res)) {
        const apiData = res.data?.list ?? [];
        const deptSet = new Set(
          apiData
            .map((d: any) => d.department_name ?? d.department)
            .filter(Boolean)
        );
        setAllDepartments([
          { value: '', label: 'Tất cả bộ phận' },
          ...Array.from(deptSet).map(d => ({ value: d as string, label: d as string })),
        ]);
      }
    };
    void loadDepartments();
  }, []);

  // === LOAD NHÂN VIÊN (SERVER-SIDE PAGINATION) ===
  const loadEmployees = useCallback(async () => {
    setIsLoadingEmployees(true);
    const params: any = {
      status: 1,
      page: currentPage,
      limit: itemsPerPage,
    };
    if (debouncedSearch.trim()) {
      params.key_search = debouncedSearch.trim();
    }

    const res = await listRef.current(params);
    if (res && isSuccess(res)) {
      const apiData = res.data?.list ?? [];
      const total = res.data?.total_record ?? 0;
      setEmployees(
        apiData.map((d: any) => ({
          id: d.id,
          name: d.name,
          department: d.department_name ?? d.department,
        }))
      );
      setTotalRecord(total);
    }
    setIsLoadingEmployees(false);
  }, [currentPage, itemsPerPage, debouncedSearch]);

  const loadAllPermissions = useCallback(async () => {
      setIsLoadingAllPermissions(true);
      const res = await listPermissionRef.current();
      if (res && isSuccess(res)) {
          const apiData = Array.isArray(res.data) ? res.data : [];
        setAllPermissions(
          apiData.map((d: any) => ({
            id: d.id,
            code: d.code,
            name: d.name,
            description: d.description,
            group: d.privilege_group && d.privilege_group.length > 0 ? d.privilege_group[0].name : 'Chưa phân loại',
          }))
        );
      }
      setIsLoadingAllPermissions(false);
  }, []);

  // === COMPUTED ===
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalRecord / itemsPerPage));
  }, [totalRecord, itemsPerPage]);

  // Lọc client-side theo department (vì API không hỗ trợ lọc theo department name)
  const filteredEmployees = useMemo(() => {
    if (!selectedDepartment) return employees;
    return employees.filter(e => e.department === selectedDepartment);
  }, [employees, selectedDepartment]);

  // === EFFECTS ===
  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    void loadAllPermissions();
  }, [loadAllPermissions]);

  // Reset page khi thay đổi search hoặc items per page
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, itemsPerPage]);

  // Auto-select nhân viên đầu tiên khi danh sách thay đổi
  useEffect(() => {
    if (isLoadingEmployees) return;
    if (filteredEmployees.length > 0) {
      const isSelectedValid = selectedEmployee && filteredEmployees.some(e => e.id === selectedEmployee.id);
      if (!isSelectedValid) {
        setSelectedEmployee(filteredEmployees[0]);
      }
    } else {
      setSelectedEmployee(null);
    }
  }, [filteredEmployees, isLoadingEmployees]);

  // Load quyền của nhân viên được chọn
  useEffect(() => {
    if (!selectedEmployee) {
      setOriginalPermissions(new Set());
      setStagedPermissions(new Set());
      return;
    }
    const loadEmployeePerms = async () => {
      setIsLoadingEmployeePermissions(true);
      const res = await fetchListPermissionsForEmployee(selectedEmployee.id);
      const apiData = (res && res.data && Array.isArray(res.data))
                    ? res.data
                    : [];
      const permIds = apiData.flatMap(item => item.privileges);
      const permsSet = new Set(permIds);
      setOriginalPermissions(permsSet);
      setStagedPermissions(permsSet);
      setIsLoadingEmployeePermissions(false);
    };
    void loadEmployeePerms();
  }, [selectedEmployee]);

  // === HANDLERS ===
  const handleSelectEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
  };

  const handleTogglePermission = (permissionId: number) => {
    setStagedPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) {
        newSet.delete(permissionId);
      } else {
        newSet.add(permissionId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!selectedEmployee) return;
    setIsSaving(true);
    const res = await updatePermissionsForEmployee(
      selectedEmployee.id,
      { privilege_ids: Array.from(stagedPermissions) }
    );
    if (res && isSuccess(res)) {
      setOriginalPermissions(stagedPermissions);
      showToast({ message: "Cập nhật quyền thành công", type: "success" });
    } else {
      showToast({ message: "Cập nhật quyền thất bại", type: "error" });
    }
    setIsSaving(false);
  };

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  }, []);

  const isDirty = useMemo(() => {
    if (originalPermissions.size !== stagedPermissions.size) return true;
    for (const id of originalPermissions) {
      if (!stagedPermissions.has(id)) return true;
    }
    return false;
  }, [originalPermissions, stagedPermissions]);

  const sortedAllPermissions = useMemo(() => {
    const filteredPermissions = !permissionSearch
      ? allPermissions
      : allPermissions.filter(p =>
          (p.name || '').toLowerCase().includes(permissionSearch.toLowerCase()) ||
          (p.description || '').toLowerCase().includes(permissionSearch.toLowerCase())
        );

    return [...filteredPermissions].sort((a, b) => {
      const groupA = a.group || '';
      const groupB = b.group || '';
      return groupA.localeCompare(groupB);
    });
  }, [allPermissions, permissionSearch]);

  return (
    <div className="flex flex-col lg:flex-row lg:h-screen w-full bg-slate-50 lg:overflow-hidden p-4 lg:p-0 gap-4 lg:gap-0">

      {/* Panel 1: Danh sách Nhân viên (Bên trái) */}
      <EmployeeListPanel
        employees={filteredEmployees}
        totalItems={totalRecord}
        isLoading={isLoadingEmployees}
        selectedEmployeeId={selectedEmployee?.id}
        onSelectEmployee={handleSelectEmployee}
        searchTerm={employeeSearch}
        onSearchChange={setEmployeeSearch}
        departments={allDepartments}
        selectedDepartment={selectedDepartment}
        onDepartmentChange={setSelectedDepartment}

        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Panel 2: Danh sách Quyền (Bên phải) */}
      <PermissionListPanel
        permissions={sortedAllPermissions}
        selectedItemName={selectedEmployee?.name}
        stagedPermissions={stagedPermissions}
        isLoadingAll={isLoadingAllPermissions}
        isLoadingItem={isLoadingEmployeePermissions}
        isSaving={isSaving}
        isDirty={isDirty}
        onTogglePermission={handleTogglePermission}
        onSave={handleSave}
        searchTerm={permissionSearch}
        onSearchChange={setPermissionSearch}
      />

    </div>
  );
};

export default PermissionEmployeeIndexPage;
